import { useEffect, useRef, useState } from "react";

// Lazy demo video: zero video bytes on initial page load.
// - preload="none" + a lightweight poster (first frame, ~17KB webp)
// - a single best-format src (webm where supported, else mp4) is attached
//   only when the video approaches the viewport, so both formats are never
//   downloaded and below-fold videos never compete with the LCP content
// - plays while visible, pauses when scrolled away (same silent-loop feel)
export default function LazyVideo({ name, testId }) {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const arm = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setArmed(true);
          arm.disconnect();
        }
      },
      { rootMargin: "250px" }
    );
    arm.observe(el);
    const vis = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.2 }
    );
    vis.observe(el);
    return () => {
      arm.disconnect();
      vis.disconnect();
    };
  }, []);

  // Once the source attaches, start playing if we're at/near the viewport
  // (the visibility observer alone can race the src attach and miss it).
  useEffect(() => {
    if (armed && ref.current) ref.current.play().catch(() => {});
  }, [armed]);

  // Pick ONE format: attaching src directly (no <source> fallback list)
  // guarantees the browser never fetches the other format.
  const src = (() => {
    if (!armed) return undefined;
    const v = document.createElement("video");
    const webm = v.canPlayType('video/webm; codecs="vp8"') || v.canPlayType("video/webm");
    return `/videos/${name}.${webm ? "webm" : "mp4"}?v=1`;
  })();

  return (
    <video
      ref={ref}
      data-testid={testId}
      muted
      loop
      playsInline
      preload="none"
      poster={`/videos/${name}-poster-240.webp?v=1`}
      src={src}
      className="w-full aspect-[9/16] object-cover rounded-xl sm:rounded-2xl select-none"
    />
  );
}
